import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, BookOpen, Beaker, Atom, Dna, Calculator, Globe, 
  Sparkles, Loader2, ChevronRight, ChevronDown, Lightbulb, Monitor,
  FileText, HelpCircle, Zap, BookMarked, Brain, Layers, PenTool,
  CheckCircle, XCircle, RefreshCw, Bot, GraduationCap, Target,
  TrendingUp, Award, Calendar, User, Building, MessageCircle,
  ClipboardList, Play, Clock, Star, Flame, BarChart3, Columns, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// A-Level Subject Configuration
const ALEVEL_SUBJECTS = {
  maths: { 
    name: 'Mathematics', 
    icon: Calculator, 
    color: { bg: 'from-blue-600 to-indigo-700', border: 'border-blue-500/30', text: 'text-blue-600', badge: 'bg-blue-500/20' },
    topics: ['Pure Mathematics', 'Statistics', 'Mechanics', 'Algebra', 'Calculus', 'Trigonometry', 'Vectors', 'Proof']
  },
  furtherMaths: { 
    name: 'Further Maths', 
    icon: Calculator, 
    color: { bg: 'from-indigo-600 to-purple-700', border: 'border-indigo-500/30', text: 'text-indigo-600', badge: 'bg-indigo-500/20' },
    topics: ['Complex Numbers', 'Matrices', 'Further Calculus', 'Polar Coordinates', 'Hyperbolic Functions', 'Differential Equations']
  },
  physics: { 
    name: 'Physics', 
    icon: Atom, 
    color: { bg: 'from-amber-500 to-orange-600', border: 'border-amber-500/30', text: 'text-amber-600', badge: 'bg-amber-500/20' },
    topics: ['Mechanics', 'Electricity', 'Waves', 'Quantum Physics', 'Nuclear Physics', 'Fields', 'Thermodynamics', 'Astrophysics']
  },
  chemistry: { 
    name: 'Chemistry', 
    icon: Beaker, 
    color: { bg: 'from-emerald-500 to-teal-600', border: 'border-emerald-500/30', text: 'text-emerald-600', badge: 'bg-emerald-500/20' },
    topics: ['Physical Chemistry', 'Organic Chemistry', 'Inorganic Chemistry', 'Analytical Chemistry', 'Electrochemistry', 'Kinetics']
  },
  biology: { 
    name: 'Biology', 
    icon: Dna, 
    color: { bg: 'from-pink-500 to-rose-600', border: 'border-pink-500/30', text: 'text-pink-600', badge: 'bg-pink-500/20' },
    topics: ['Cell Biology', 'Genetics', 'Ecology', 'Physiology', 'Biochemistry', 'Evolution', 'Microbiology', 'Immunology']
  },
  economics: { 
    name: 'Economics', 
    icon: TrendingUp, 
    color: { bg: 'from-green-500 to-lime-600', border: 'border-green-500/30', text: 'text-green-600', badge: 'bg-green-500/20' },
    topics: ['Microeconomics', 'Macroeconomics', 'International Trade', 'Market Structures', 'Monetary Policy', 'Fiscal Policy']
  },
  computerScience: { 
    name: 'Computer Science', 
    icon: Monitor, 
    color: { bg: 'from-cyan-500 to-blue-600', border: 'border-cyan-500/30', text: 'text-cyan-600', badge: 'bg-cyan-500/20' },
    topics: ['Programming', 'Data Structures', 'Algorithms', 'Databases', 'Networks', 'Computer Architecture', 'Cybersecurity']
  },
  englishLit: { 
    name: 'English Literature', 
    icon: BookOpen, 
    color: { bg: 'from-rose-500 to-red-600', border: 'border-rose-500/30', text: 'text-rose-600', badge: 'bg-rose-500/20' },
    topics: ['Shakespeare', 'Poetry', 'Prose Fiction', 'Drama', 'Literary Theory', 'Comparative Literature', 'Gothic Literature']
  },
  history: { 
    name: 'History', 
    icon: Globe, 
    color: { bg: 'from-yellow-500 to-amber-600', border: 'border-yellow-500/30', text: 'text-yellow-400', badge: 'bg-yellow-500/20' },
    topics: ['British History', 'European History', 'World Wars', 'Cold War', 'Medieval History', 'Tudor Period', 'Modern History']
  }
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
};

const EdsALevelHub = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('revision');
  const [selectedSubject, setSelectedSubject] = useState('maths');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingType, setLoadingType] = useState(null); // Track which tool is loading: 'flashcards', 'summary', 'explanation', 'practice'
  const [generatedContent, setGeneratedContent] = useState(null);
  const [practiceMode, setPracticeMode] = useState(false);
  
  // Custom search state
  const [customSearch, setCustomSearch] = useState('');
  const [useCustomSearch, setUseCustomSearch] = useState(false);
  
  // UniGuide state
  const [profile, setProfile] = useState(null);
  const [universities, setUniversities] = useState([]);
  const [shortlist, setShortlist] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(false);

  const token = localStorage.getItem('token');
  const colors = ALEVEL_SUBJECTS[selectedSubject]?.color || ALEVEL_SUBJECTS.maths.color;
  const SubjectIcon = ALEVEL_SUBJECTS[selectedSubject]?.icon || Calculator;

  useEffect(() => {
    if (activeTab === 'uniguide') {
      loadUniGuideData();
    }
  }, [activeTab]);

  const loadUniGuideData = async () => {
    if (!token) return;
    setLoadingProfile(true);
    try {
      const [profileRes, uniRes, shortlistRes] = await Promise.all([
        axios.get(`${API}/api/pathways/profile`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/api/pathways/universities`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/pathways/shortlist`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setProfile(profileRes.data);
      setUniversities(uniRes.data || []);
      setShortlist(shortlistRes.data || []);
    } catch (error) {
      console.error('Failed to load UniGuide data');
    } finally {
      setLoadingProfile(false);
    }
  };

  const generateTopicContent = async (topic, type = 'explanation', isCustom = false) => {
    setLoading(true);
    setLoadingType(type); // Track which tool is loading
    if (!isCustom) {
      setSelectedTopic(topic);
    }
    try {
      const subjectToUse = isCustom ? 'Custom Search' : selectedSubject;
      const subjectName = isCustom ? 'Custom Topic' : ALEVEL_SUBJECTS[selectedSubject].name;
      
      const response = await axios.post(
        `${API}/api/alevel-hub/generate-content`,
        {
          subject: subjectToUse,
          topic: topic,
          content_type: type
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setGeneratedContent({
        topic: response.data.topic,
        subject: subjectName,
        type: response.data.content_type,
        content: response.data.content,
        provider: response.data.provider
      });
      
      toast.success(`${type === 'explanation' ? 'Explanation' : type === 'practice' ? 'Practice questions' : type === 'flashcards' ? 'Flashcards' : 'Summary'} generated!`);
    } catch (error) {
      console.error('Failed to generate content:', error);
      toast.error('Failed to generate content. Please try again.');
    } finally {
      setLoading(false);
      setLoadingType(null); // Clear loading type
    }
  };
  
  // Handle custom search submission
  const handleCustomSearch = (type = 'explanation') => {
    if (!customSearch.trim()) {
      toast.error('Please enter a subject or topic to search');
      return;
    }
    setUseCustomSearch(true);
    setSelectedTopic(null);
    generateTopicContent(customSearch.trim(), type, true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100/50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate(-1)}
                className="text-slate-500 hover:text-slate-900 hover:bg-slate-100 px-2 sm:px-3"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Go Back</span>
              </Button>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl bg-gradient-to-br from-orange-500 to-red-600">
                  <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6 text-slate-900" />
                </div>
                <div>
                  <h1 className="text-base sm:text-xl font-bold text-slate-900 flex items-center gap-1 sm:gap-2">
                    Eds A-Level Hub™
                    <Badge className="bg-green-500/20 text-green-600 text-[10px] sm:text-xs hidden sm:inline-flex">NEW</Badge>
                  </h1>
                  <p className="text-[10px] sm:text-xs text-slate-500 hidden sm:block">Study • Practice • Succeed</p>
                </div>
              </div>
            </div>
            <Badge className="bg-orange-500/20 text-orange-600 border-0 text-[10px] sm:text-xs">
              Year 12-13
            </Badge>
          </div>
        </div>
      </header>

      {/* Main Navigation Tabs - Mobile Scrollable */}
      <div className="sticky top-[57px] sm:top-[73px] z-40 bg-white backdrop-blur-lg border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-transparent border-0 h-auto p-1 gap-1 inline-flex w-auto min-w-full sm:w-full">
              <TabsTrigger 
                value="revision" 
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-teal-500 data-[state=active]:text-white px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
              >
                <Brain className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                Revision Hub
              </TabsTrigger>
              {/* Practice Papers - Hidden until question bank matures */}
              {/* 
              <TabsTrigger 
                value="practice" 
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-500 data-[state=active]:text-white px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
              >
                <FileText className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                Practice Papers
              </TabsTrigger>
              */}
              <TabsTrigger 
                value="uniguide" 
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:via-purple-500 data-[state=active]:to-pink-500 data-[state=active]:text-white px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
              >
                <GraduationCap className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                UniGuide
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">

        {/* Practice Papers Tab - Hidden until question bank matures */}
        {false && activeTab === 'practice' && (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="space-y-4 sm:space-y-6"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Practice Papers</h2>
              <p className="text-sm sm:text-base text-slate-500 mb-4 sm:mb-6">Test your knowledge with exam-style questions</p>
            </motion.div>

            {/* Subject Selection for Practice */}
            <motion.div variants={fadeInUp} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {Object.entries(ALEVEL_SUBJECTS).map(([key, subject]) => {
                const Icon = subject.icon;
                // Map subject keys to paper library format (matches backend a-levels subject mappings)
                const subjectMap = {
                  'maths': 'maths',
                  'furtherMaths': 'further-maths',
                  'physics': 'physics',
                  'chemistry': 'chemistry',
                  'biology': 'biology',
                  'economics': 'economics',
                  'computerScience': 'computer-science',
                  'englishLit': 'english-lit',
                  'history': 'history'
                };
                return (
                  <Card 
                    key={key}
                    className={`bg-slate-100 ${subject.color.border} border hover:border-opacity-60 cursor-pointer transition-all hover:scale-[1.02]`}
                    onClick={() => navigate(`/papers/a-levels/${subjectMap[key]}`, { state: { fromALevelHub: true } })}
                    data-testid={`practice-paper-${key}`}
                  >
                    <CardContent className="p-4 sm:p-6 text-center">
                      <div className={`inline-flex p-3 sm:p-4 rounded-xl bg-gradient-to-br ${subject.color.bg} mb-3 sm:mb-4`}>
                        <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-slate-900" />
                      </div>
                      <h3 className="font-semibold text-slate-900 text-sm sm:text-base mb-2">{subject.name}</h3>
                      <div className="flex justify-center gap-1 sm:gap-2 text-[10px] sm:text-xs mb-3 sm:mb-4">
                        <Badge className="bg-blue-500/20 text-blue-600 px-1.5 sm:px-2">Practice</Badge>
                        <Badge className="bg-purple-500/20 text-purple-600 px-1.5 sm:px-2">Revision</Badge>
                      </div>
                      <Button 
                        className={`w-full bg-gradient-to-r ${subject.color.bg} text-xs sm:text-sm`}
                        size="sm"
                      >
                        <Play className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" /> Start
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </motion.div>
            
            {/* Quick Access to Paper Types */}
            <motion.div variants={fadeInUp} className="mt-6 sm:mt-8">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-3 sm:mb-4">Quick Access</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                <Card className="bg-slate-100 border-blue-500/30 border hover:border-blue-500/50 cursor-pointer transition-all" onClick={() => navigate('/papers/a-levels/maths', { state: { fromALevelHub: true } })}>
                  <CardContent className="p-4 text-center">
                    <FileText className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                    <p className="text-slate-900 text-sm font-medium">Maths Papers</p>
                    <p className="text-slate-500 text-xs">Practice & Revision</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-100 border-purple-500/30 border hover:border-purple-500/50 cursor-pointer transition-all" onClick={() => navigate('/papers/a-levels/physics', { state: { fromALevelHub: true } })}>
                  <CardContent className="p-4 text-center">
                    <Clock className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                    <p className="text-slate-900 text-sm font-medium">Physics Papers</p>
                    <p className="text-slate-500 text-xs">Practice & Revision</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-100 border-amber-500/30 border hover:border-amber-500/50 cursor-pointer transition-all" onClick={() => navigate('/papers/a-levels/chemistry', { state: { fromALevelHub: true } })}>
                  <CardContent className="p-4 text-center">
                    <Star className="h-6 w-6 text-amber-600 mx-auto mb-2" />
                    <p className="text-slate-900 text-sm font-medium">Chemistry Papers</p>
                    <p className="text-slate-500 text-xs">Practice & Revision</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-100 border-emerald-500/30 border hover:border-emerald-500/50 cursor-pointer transition-all" onClick={() => navigate('/challenger')}>
                  <CardContent className="p-4 text-center">
                    <Flame className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
                    <p className="text-slate-900 text-sm font-medium">Challenger</p>
                    <p className="text-slate-500 text-xs">Compete & earn</p>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Revision Tab */}
        {activeTab === 'revision' && (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="space-y-6"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Revision Tools</h2>
              <p className="text-sm sm:text-base text-slate-500 mb-4">Select a subject and use AI-powered revision tools</p>
            </motion.div>

            {/* Subject Selection for Revision - Mobile Scrollable */}
            <motion.div variants={fadeInUp} className="mb-6">
              <h3 className="text-sm font-medium text-slate-500 mb-3">Choose Subject:</h3>
              <div className="overflow-x-auto -mx-3 px-3 pb-2 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="flex gap-2 min-w-max sm:flex-wrap sm:min-w-0">
                  {Object.entries(ALEVEL_SUBJECTS).map(([key, subject]) => {
                    const Icon = subject.icon;
                    const isActive = selectedSubject === key;
                    return (
                      <Button
                        key={key}
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedSubject(key)}
                        className={`whitespace-nowrap text-xs sm:text-sm ${
                          isActive 
                            ? `bg-gradient-to-r ${subject.color.bg} text-white` 
                            : 'border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                        }`}
                      >
                        <Icon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> {subject.name}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </motion.div>

            {/* Topic Selection - Mobile Scrollable */}
            <motion.div variants={fadeInUp} className="mb-6">
              <h3 className="text-sm font-medium text-slate-500 mb-3">Choose Topic:</h3>
              <div className="overflow-x-auto -mx-3 px-3 pb-2 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="flex flex-wrap sm:flex-wrap gap-2 min-w-max sm:min-w-0">
                  {ALEVEL_SUBJECTS[selectedSubject].topics.map((topic) => (
                    <Button
                      key={topic}
                      variant={selectedTopic === topic ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setSelectedTopic(topic);
                        setUseCustomSearch(false);
                        setCustomSearch('');
                      }}
                      className={`whitespace-nowrap text-xs sm:text-sm ${
                        selectedTopic === topic 
                          ? `bg-gradient-to-r ${colors.bg} text-white` 
                          : 'border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                    >
                      {topic}
                    </Button>
                  ))}
                </div>
              </div>
              <p className="text-[10px] text-slate-500 mt-1 sm:hidden">Scroll to see more topics →</p>
            </motion.div>

            {/* Custom Search - Free Text */}
            <motion.div variants={fadeInUp} className="mb-6">
              <Card className="bg-gradient-to-r from-violet-500/10 to-purple-500/10 border-violet-500/30 border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Search className="h-4 w-4 text-violet-600" />
                    <h3 className="text-sm font-medium text-slate-900">Can't find your topic?</h3>
                    <Badge className="bg-violet-500/20 text-violet-600 text-[10px]">AI Search</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">
                    Search any subject or topic - our AI will generate content for anything you need!
                  </p>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        placeholder="e.g., 'Quantum Physics', 'French Revolution', 'Python Programming'..."
                        value={customSearch}
                        onChange={(e) => setCustomSearch(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleCustomSearch('explanation')}
                        className="bg-slate-100 border-slate-200 text-slate-900 placeholder:text-slate-500 pr-10"
                      />
                      {customSearch && (
                        <button
                          onClick={() => setCustomSearch('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                        >
                          <XCircle className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <Button
                      onClick={() => handleCustomSearch('explanation')}
                      disabled={!customSearch.trim() || loading}
                      className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
                    >
                      {loading && useCustomSearch ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          <span className="hidden sm:inline">Generate</span>
                        </>
                      )}
                    </Button>
                  </div>
                  {customSearch.trim() && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className="text-xs text-slate-500">Quick actions:</span>
                      <button
                        onClick={() => handleCustomSearch('summary')}
                        disabled={loading}
                        className="text-xs text-emerald-600 hover:text-emerald-600 underline"
                      >
                        Summary
                      </button>
                      <button
                        onClick={() => handleCustomSearch('flashcards')}
                        disabled={loading}
                        className="text-xs text-amber-600 hover:text-amber-600 underline"
                      >
                        Flashcards
                      </button>
                      <button
                        onClick={() => handleCustomSearch('practice')}
                        disabled={loading}
                        className="text-xs text-pink-600 hover:text-pink-600 underline"
                      >
                        Practice Qs
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Revision Tools Grid */}
            <motion.div variants={fadeInUp} className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              {/* Flashcards */}
              <Card className="bg-slate-100 border-amber-500/30 border hover:border-amber-500/50 transition-all">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600">
                      <Layers className="h-5 w-5 sm:h-6 sm:w-6 text-slate-900" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Flashcards</h3>
                      <p className="text-[10px] sm:text-xs text-slate-500">Quick recall</p>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-xs sm:text-sm"
                    onClick={() => selectedTopic && generateTopicContent(selectedTopic, 'flashcards')}
                    disabled={!selectedTopic || loading}
                  >
                    {loadingType === 'flashcards' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
                    Generate Cards
                  </Button>
                </CardContent>
              </Card>

              {/* Topic Summary */}
              <Card className="bg-slate-100 border-emerald-500/30 border hover:border-emerald-500/50 transition-all">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600">
                      <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-slate-900" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Summary</h3>
                      <p className="text-[10px] sm:text-xs text-slate-500">Key points</p>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-xs sm:text-sm"
                    onClick={() => selectedTopic && generateTopicContent(selectedTopic, 'summary')}
                    disabled={!selectedTopic || loading}
                  >
                    {loadingType === 'summary' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BookOpen className="h-4 w-4 mr-2" />}
                    Get Summary
                  </Button>
                </CardContent>
              </Card>

              {/* Full Explanation */}
              <Card className="bg-slate-100 border-blue-500/30 border hover:border-blue-500/50 transition-all">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600">
                      <Brain className="h-5 w-5 sm:h-6 sm:w-6 text-slate-900" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Learn Topic</h3>
                      <p className="text-[10px] sm:text-xs text-slate-500">Full explanation</p>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-xs sm:text-sm"
                    onClick={() => selectedTopic && generateTopicContent(selectedTopic, 'explanation')}
                    disabled={!selectedTopic || loading}
                  >
                    {loadingType === 'explanation' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lightbulb className="h-4 w-4 mr-2" />}
                    Learn Now
                  </Button>
                </CardContent>
              </Card>

              {/* Practice Questions */}
              <Card className="bg-slate-100 border-purple-500/30 border hover:border-purple-500/50 transition-all">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600">
                      <Zap className="h-5 w-5 sm:h-6 sm:w-6 text-slate-900" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Practice</h3>
                      <p className="text-[10px] sm:text-xs text-slate-500">Test yourself</p>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-600 text-xs sm:text-sm"
                    onClick={() => selectedTopic && generateTopicContent(selectedTopic, 'practice')}
                    disabled={!selectedTopic || loading}
                  >
                    {loadingType === 'practice' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Target className="h-4 w-4 mr-2" />}
                    Practice Qs
                  </Button>
                </CardContent>
              </Card>

              {/* Challenger Mode */}
              <Card className="bg-slate-100 border-cyan-500/30 border hover:border-cyan-500/50 transition-all">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600">
                      <Flame className="h-5 w-5 sm:h-6 sm:w-6 text-slate-900" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Challenger</h3>
                      <p className="text-[10px] sm:text-xs text-slate-500">Compete</p>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-xs sm:text-sm"
                    onClick={() => navigate(`/challenger/a-level/${selectedSubject || 'maths'}`)}
                    disabled={!selectedSubject}
                  >
                    <Star className="h-4 w-4 mr-2" />
                    Challenge
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Prompt to select topic */}
            {!selectedTopic && (
              <motion.div variants={fadeInUp} className="text-center py-8 bg-slate-100 rounded-xl border border-dashed border-slate-200">
                <Lightbulb className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                <p className="text-slate-500">Select a topic above to use the revision tools</p>
              </motion.div>
            )}

            {/* Generated Content Display */}
            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
                <span className="ml-3 text-slate-500">Generating revision content...</span>
              </div>
            )}

            {generatedContent && !loading && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6"
              >
                <Card className={`bg-slate-100 ${colors.border} border`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-slate-900 flex items-center gap-2">
                          {generatedContent.type === 'flashcards' && <Layers className="text-amber-600" />}
                          {generatedContent.type === 'summary' && <FileText className="text-emerald-600" />}
                          {generatedContent.type === 'explanation' && <Brain className="text-blue-600" />}
                          {generatedContent.type === 'practice' && <Zap className="text-purple-600" />}
                          {generatedContent.topic}
                        </CardTitle>
                        <CardDescription className="text-slate-500 text-sm">
                          {generatedContent.subject} • {generatedContent.type.charAt(0).toUpperCase() + generatedContent.type.slice(1)}
                        </CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setGeneratedContent(null)} className="text-slate-500">✕</Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-invert prose-sm max-w-none">
                      <div className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">
                        {generatedContent.content.split('\n').map((line, i) => {
                          if (line.startsWith('## ')) return <h2 key={i} className="text-lg font-bold text-slate-900 mt-4 mb-2">{line.replace('## ', '')}</h2>;
                          if (line.startsWith('### ')) return <h3 key={i} className="text-base font-semibold text-slate-900 mt-3 mb-1">{line.replace('### ', '')}</h3>;
                          if (line.startsWith('**') && line.endsWith('**')) return <p key={i} className="font-semibold text-slate-900 mt-2">{line.replace(/\*\*/g, '')}</p>;
                          if (line.startsWith('- ') || line.startsWith('• ')) return <li key={i} className="ml-4 text-slate-600">{line.replace(/^[-•]\s/, '')}</li>;
                          if (line.trim() === '') return <br key={i} />;
                          const parts = line.split(/(\*\*[^*]+\*\*)/g);
                          return <p key={i} className="text-slate-600">{parts.map((part, j) => part.startsWith('**') && part.endsWith('**') ? <strong key={j} className="text-slate-900">{part.replace(/\*\*/g, '')}</strong> : part)}</p>;
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* UniGuide Tab */}
        {activeTab === 'uniguide' && (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="space-y-6"
          >
            {/* UniGuide Header */}
            <motion.div variants={fadeInUp} className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 border border-indigo-500/20 mb-4">
                <GraduationCap className="h-5 w-5 text-indigo-600" />
                <span className="text-indigo-600 font-medium">Eds UniGuide™</span>
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                From Ambition to Admission
              </h2>
              <p className="text-slate-500 max-w-2xl mx-auto">
                Your AI-powered university counsellor. Get personalized guidance for UCAS applications, 
                university matching, and interview preparation.
              </p>
            </motion.div>

            {/* UniGuide Feature Cards - Always show */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Profile Card */}
              <motion.div variants={fadeInUp}>
                <Card className="bg-slate-100 border-indigo-500/30 border h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600">
                        <User className="h-6 w-6 text-slate-900" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Your Profile</h3>
                        <p className="text-xs text-slate-500">A-Level subjects & grades</p>
                      </div>
                    </div>
                    <p className="text-slate-500 text-sm mb-4">
                      Build your academic profile with A-Level subjects, predicted grades, and extracurriculars.
                    </p>
                    <Button 
                      className="w-full bg-gradient-to-r from-indigo-500 to-purple-600"
                      onClick={() => navigate('/pathways')}
                    >
                      <PenTool className="h-4 w-4 mr-2" /> Build Profile
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* University Matching */}
              <motion.div variants={fadeInUp}>
                <Card className="bg-slate-100 border-purple-500/30 border h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600">
                        <Building className="h-6 w-6 text-slate-900" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">University Matching</h3>
                        <p className="text-xs text-slate-500">Find your best fit</p>
                      </div>
                    </div>
                    <p className="text-slate-500 text-sm mb-4">
                      AI-powered matching with 36+ UK universities based on your profile and aspirations.
                    </p>
                    <Button 
                      className="w-full bg-gradient-to-r from-purple-500 to-pink-600"
                      onClick={() => navigate('/pathways')}
                    >
                      <Target className="h-4 w-4 mr-2" /> Find Universities
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* University Comparison - NEW */}
              <motion.div variants={fadeInUp}>
                <Card className="bg-slate-100 border-teal-500/30 border h-full relative overflow-hidden">
                  <div className="absolute top-2 right-2">
                    <Badge className="bg-teal-500/20 text-teal-600 text-[10px]">NEW</Badge>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600">
                        <Columns className="h-6 w-6 text-slate-900" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Compare Universities</h3>
                        <p className="text-xs text-slate-500">Side-by-side analysis</p>
                      </div>
                    </div>
                    <p className="text-slate-500 text-sm mb-4">
                      Compare up to 4 universities side-by-side on rankings, entry requirements, courses & more.
                    </p>
                    <Button 
                      className="w-full bg-gradient-to-r from-teal-500 to-cyan-600"
                      onClick={() => navigate('/pathways?tab=compare')}
                    >
                      <Columns className="h-4 w-4 mr-2" /> Compare Now
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Interview Prep */}
              <motion.div variants={fadeInUp}>
                <Card className="bg-slate-100 border-green-500/30 border h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600">
                        <MessageCircle className="h-6 w-6 text-slate-900" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Interview Prep</h3>
                        <p className="text-xs text-slate-500">AI mock interviews</p>
                      </div>
                    </div>
                    <p className="text-slate-500 text-sm mb-4">
                      Practice with AI-generated interview questions tailored to your chosen courses.
                    </p>
                    <Button 
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600"
                      onClick={() => navigate('/pathways')}
                    >
                      <Sparkles className="h-4 w-4 mr-2" /> Start Practice
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Personal Statement */}
              <motion.div variants={fadeInUp}>
                <Card className="bg-slate-100 border-amber-500/30 border h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600">
                        <FileText className="h-6 w-6 text-slate-900" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Personal Statement</h3>
                        <p className="text-xs text-slate-500">AI writing assistant</p>
                      </div>
                    </div>
                    <p className="text-slate-500 text-sm mb-4">
                      Get AI-powered help crafting your perfect UCAS personal statement.
                    </p>
                    <Button 
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-600"
                      onClick={() => navigate('/pathways')}
                    >
                      <PenTool className="h-4 w-4 mr-2" /> Write Statement
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Analytics */}
              <motion.div variants={fadeInUp}>
                <Card className="bg-slate-100 border-cyan-500/30 border h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600">
                        <TrendingUp className="h-6 w-6 text-slate-900" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Predictive Analytics</h3>
                        <p className="text-xs text-slate-500">Success probability</p>
                      </div>
                    </div>
                    <p className="text-slate-500 text-sm mb-4">
                      See your chances of admission based on your profile and university targets.
                    </p>
                    <Button 
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-600"
                      onClick={() => navigate('/pathways')}
                    >
                      <BarChart3 className="h-4 w-4 mr-2" /> View Analytics
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* UCAS Tracker */}
              <motion.div variants={fadeInUp}>
                <Card className="bg-slate-100 border-rose-500/30 border h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-xl bg-gradient-to-br from-rose-500 to-red-600">
                        <ClipboardList className="h-6 w-6 text-slate-900" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">UCAS Tracker</h3>
                        <p className="text-xs text-slate-500">Deadlines & tasks</p>
                      </div>
                    </div>
                    <p className="text-slate-500 text-sm mb-4">
                      Stay on top of important UCAS dates and application milestones.
                    </p>
                    <Button 
                      className="w-full bg-gradient-to-r from-rose-500 to-red-600"
                      onClick={() => navigate('/pathways')}
                    >
                      <Calendar className="h-4 w-4 mr-2" /> View Deadlines
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Full UniGuide Access Button */}
            <motion.div variants={fadeInUp} className="text-center mt-8">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 shadow-lg shadow-indigo-500/25"
                onClick={() => navigate('/pathways')}
              >
                <GraduationCap className="h-5 w-5 mr-2" />
                Open Full Eds UniGuide™
                <ChevronRight className="h-5 w-5 ml-2" />
              </Button>
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default EdsALevelHub;
